// apps/web/src/components/dashboard/HelpDrawer.tsx
//
// Help drawer per spec §3.3. Opens from a tile's help button. Renders
// per-tile copy + tier definitions + data-source documentation.
//
// Backed by shadcn/ui Sheet (a slide-in panel). If the Sheet primitive
// isn't yet added to apps/web/src/components/ui/, run the project's
// shadcn add-component command and re-run lint/typecheck.

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

export interface HelpDrawerTierDefinition {
  label: string
  meaning: string
}

export interface HelpDrawerEntry {
  id: string
  title: string
  whatItShows: string
  whatYouDecide: string
  dataSource: string
  tierDefinitions?: HelpDrawerTierDefinition[]
}

export interface HelpDrawerProps {
  activeTileId: string | null
  entry: HelpDrawerEntry | null
  onClose: () => void
}

export function HelpDrawer(props: HelpDrawerProps) {
  const open = props.activeTileId !== null && props.entry !== null

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) props.onClose()
      }}
    >
      <SheetContent side="right" className="w-[420px] sm:w-[480px]">
        {props.entry ? (
          <div className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>{props.entry.title}</SheetTitle>
            </SheetHeader>

            <div className="mt-6 space-y-6 overflow-y-auto pr-2">
              <section>
                <h4 className="text-sm font-semibold text-muted-foreground">
                  What this tile shows
                </h4>
                <p className="mt-1 text-sm">{props.entry.whatItShows}</p>
              </section>

              <section>
                <h4 className="text-sm font-semibold text-muted-foreground">
                  What you can decide
                </h4>
                <p className="mt-1 text-sm">{props.entry.whatYouDecide}</p>
              </section>

              <section>
                <h4 className="text-sm font-semibold text-muted-foreground">
                  Data source
                </h4>
                <p className="mt-1 font-mono text-xs">
                  {props.entry.dataSource}
                </p>
              </section>

              {props.entry.tierDefinitions?.length ? (
                <section>
                  <h4 className="text-sm font-semibold text-muted-foreground">
                    Tier definitions
                  </h4>
                  <dl className="mt-2 space-y-2 text-sm">
                    {props.entry.tierDefinitions.map((t) => (
                      <div key={t.label}>
                        <dt className="font-mono text-xs uppercase">
                          {t.label}
                        </dt>
                        <dd className="text-muted-foreground">{t.meaning}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ) : null}
            </div>

            <div className="mt-auto pt-4">
              <SheetClose asChild>
                <Button
                  variant="outline"
                  aria-label="Close"
                  onClick={props.onClose}
                >
                  <X className="mr-2 h-4 w-4" /> Close
                </Button>
              </SheetClose>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
